import {
  Button,
  Input,
  Modal,
  Select,
  type SelectOption,
} from "@omnitok/ui";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { resolveLocalized } from "@pervasivemind/dashboard-render";

import { IconPicker } from "@/components/IconPicker";
import { useT } from "@/i18n";
import { HttpError } from "@/lib/api";
import { draftsApi } from "@/lib/builder-api";
import type { Project, ReportConfig, ReportDraft } from "@/types";

const KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

const NO_PROJECT_VALUE = "__none__";

export function isValidReportKey(key: string): boolean {
  const trimmed = key.trim();
  return KEY_PATTERN.test(trimmed) && trimmed.length >= 2;
}

/**
 * Create needs a valid key AND a display name. The name used to fall
 * back to the key; it is known when the report is created, so it is
 * asked for instead.
 */
export function canCreateDraft(key: string, displayName: string): boolean {
  return isValidReportKey(key) && displayName.trim() !== "";
}

/**
 * The ``report_config`` a new report starts with.
 *
 * ``duckdb_table`` is written ONLY when the admin typed one. It used to
 * default to the report KEY, and a report that later got a view source
 * kept naming a table that never existed (staging, 2026-09-11:
 * ``unauthorized_monthly`` vs its view ``unauthorized_listings``).
 * Without the field, the first source declared in the Data tab defines
 * the table — the engine, the publish validator and the editor's column
 * pickers all read it from there — and a report with neither is refused
 * at publish ("duckdb_table is required").
 */
export function newReportConfig(
  displayName: string,
  duckdbTable: string,
  icon: string,
): ReportConfig {
  const table = duckdbTable.trim();
  return {
    display_name: displayName.trim(),
    ...(table !== "" ? { duckdb_table: table } : {}),
    ...(icon ? { icon } : {}),
  };
}

interface CreateDraftModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (draft: ReportDraft) => void;
  projects: Project[];
  /**
   * When present, the project select is pre-filled with this project
   * ID. Used by the "+ report" button in the project hub so the user
   * doesn't have to re-select the project context.
   */
  presetProjectId: string | null;
}

/**
 * Create-report modal — extracted verbatim from the retired
 * ``BuilderListPage`` (PR-0 builder reorg). The legacy ``?template=``
 * handling died with that page: templates were removed in the
 * 2026-05-18 audit revisit and new reports are always blank v2 drafts.
 */
export function CreateDraftModal({
  open,
  onClose,
  onCreated,
  projects,
  presetProjectId,
}: CreateDraftModalProps): JSX.Element {
  const { t, locale } = useT();
  const [key, setKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [duckdbTable, setDuckdbTable] = useState("");
  const [icon, setIcon] = useState("");
  const [projectId, setProjectId] = useState(NO_PROJECT_VALUE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form whenever the modal opens; seed the project selector
  // with the hub's project when provided.
  useEffect(() => {
    if (open) {
      setKey("");
      setDisplayName("");
      setDuckdbTable("");
      setIcon("");
      setProjectId(presetProjectId ?? NO_PROJECT_VALUE);
      setError(null);
      setSubmitting(false);
    }
  }, [open, presetProjectId]);

  const trimmedKey = key.trim();
  const keyValid = isValidReportKey(key);
  const canCreate = canCreateDraft(key, displayName);

  const projectOptions = useMemo<SelectOption[]>(
    () => [
      { value: NO_PROJECT_VALUE, label: t("list.create.project.none") },
      ...projects.map((p) => ({
        value: p.id,
        label: resolveLocalized(p.name, locale),
      })),
    ],
    [projects, t, locale],
  );

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (!canCreate) return;
    setError(null);
    setSubmitting(true);
    try {
      const selectedProject =
        projectId && projectId !== NO_PROJECT_VALUE ? projectId : null;
      const created = await draftsApi.create({
        key: trimmedKey,
        template_slug: null,
        project_id: selectedProject,
        report_config: newReportConfig(displayName, duckdbTable, icon),
        dashboard_config: { version: 2, global_filters: [], sections: [] },
      });
      onCreated(created);
    } catch (err) {
      if (err instanceof HttpError && err.status === 409) {
        setError(t("list.create.keyExists", { key: trimmedKey }));
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t("common.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => (!submitting ? onClose() : undefined)}
      title={t("list.create.title")}
      size="md"
      footer={
        <>
          <Button
            ghost
            onClick={onClose}
            disabled={submitting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="create-draft-form"
            variant="primary"
            disabled={submitting || !canCreate}
            loading={submitting}
          >
            {t("common.create")}
          </Button>
        </>
      }
    >
      <form
        id="create-draft-form"
        onSubmit={handleSubmit}
        className="space-y-4"
        noValidate
      >
        <Select
          label={t("list.create.project")}
          options={projectOptions}
          value={projectId}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setProjectId(e.target.value)
          }
          fullWidth
        />

        <Input
          label={t("list.create.key")}
          value={key}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setKey(e.target.value)
          }
          placeholder="my_new_report"
          autoFocus
          disabled={submitting}
          fullWidth
          error={
            !keyValid && key.length > 0
              ? t("list.create.keyInvalid")
              : undefined
          }
        />

        <Input
          label={t("list.create.name")}
          value={displayName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setDisplayName(e.target.value)
          }
          placeholder="My New Report"
          required
          disabled={submitting}
          fullWidth
        />

        <Input
          label={t("list.create.table")}
          helperText={t("list.create.tableHint")}
          value={duckdbTable}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setDuckdbTable(e.target.value)
          }
          disabled={submitting}
          fullWidth
        />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-neutral-700">
            {t("list.create.icon")}
          </label>
          <IconPicker value={icon} onChange={setIcon} disabled={submitting} />
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-error/40 bg-error/5 px-3 py-2 text-sm text-error-dark"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
